import { DatePipe } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import { ApplicationsService } from '../../../orval-gen/endpoints/applications/applications.service';
import { FilterPermissionsPipe } from '../../pipes/filter-permissions.pipe';
import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { LocalStorageService } from '../../shared/services/local-storage.service';
import { unwrap } from '../../utils/http-utils';
import {
  Application,
  appInitials,
  canDrop,
  connectionBadge,
  toggleConfirmation,
  toggleConnection,
  toggleLabel,
  toggleSuccessMessage,
} from '../applications/application-utils';

/**
 * Single application view: details, access and — for enabled apps only — its API key.
 * There is no per-app endpoint, so the app is picked from the same list the Applications page
 * loads (GET /agency/applications).
 */
@Component({
  selector: 'app-application-detail',
  imports: [DatePipe, RouterLink, FilterPermissionsPipe, IconComponent, ConfirmDialogComponent],
  templateUrl: './application-detail.component.html',
})
export class ApplicationDetailComponent implements OnInit {
  private readonly applicationsService = inject(ApplicationsService);
  private readonly toastr = inject(ToastrService);
  private readonly route = inject(ActivatedRoute);

  readonly initials = appInitials;
  readonly badge = connectionBadge;
  readonly toggleLabel = toggleLabel;
  readonly canDrop = canDrop;
  /** The connections are the consumer's, so only managers of its super agency may see or change them. */
  readonly canManage = inject(LocalStorageService).getBool('isConsumerOwner');

  appId = '';
  app: Application | null = null;
  loaded = false;
  saving = false;
  modalVisible = false;
  revealed = false;

  ngOnInit(): void {
    this.appId = this.route.snapshot.paramMap.get('appId') ?? '';
    if (this.canManage) {
      this.load();
    } else {
      this.loaded = true;
    }
  }

  load(): void {
    this.loaded = false;
    this.applicationsService
      .agencyApplicationsList()
      .pipe(
        unwrap(),
        finalize(() => (this.loaded = true))
      )
      .subscribe({
        next: apps => (this.app = (apps ?? []).find(a => a.id === this.appId) ?? null),
        error: error => console.error('Failed to load applications:', error),
      });
  }

  get confirmation(): { title: string; message: string } {
    return this.app ? toggleConfirmation(this.app) : { title: '', message: '' };
  }

  /** Back to the tab the app is listed on: its connection's status, or Available without one. */
  get backTab(): { tab?: string } {
    const tab = this.app ? (this.app.connection?.status ?? 'available') : null;
    return tab && tab !== 'active' ? { tab } : {};
  }

  /** The key the app calls with on the agency's behalf, issued once the developer approves. */
  get apiKey(): string {
    return this.app?.connection?.status === 'active' ? this.app.connection.consumer_key : '';
  }

  confirmToggle(): void {
    const app = this.app;
    if (!app) return;
    this.saving = true;
    toggleConnection(this.applicationsService, app)
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: () => {
          this.modalVisible = false;
          this.revealed = false;
          this.toastr.success(toggleSuccessMessage(app), 'Success');
          this.load();
        },
        error: err => {
          console.error('Failed to update application status:', err);
          this.modalVisible = false;
        },
      });
  }

  mask(value: string): string {
    if (!value) return '';
    return value.length <= 8
      ? '••••••••'
      : `${value.slice(0, 4)}${'•'.repeat(16)}${value.slice(-4)}`;
  }

  copy(value: string): void {
    navigator.clipboard.writeText(value).then(
      () => this.toastr.success('', 'Copied to clipboard'),
      () => this.toastr.error('Could not copy to the clipboard', 'Error')
    );
  }
}
